import { createSlug } from './urlHelpers';
import { createResourceLocatorString, findRouteByRouteName } from './routes';
import { getCartItemCount, getSellerCartListingIds } from '../config/configCart';
import { getCartDeliveryOptions } from './shippingOptions';

/**
 * Whether every listing has stock for the quantity in this seller's cart.
 *
 * @param {Object} sellerCart - { listingId: {count}, deliveryMethod }
 * @param {Array} sellerListings - that seller's cart listings, with currentStock
 * @returns {boolean}
 */
export const hasStockForSellerCart = (sellerCart, sellerListings) =>
  sellerListings.every(l => {
    const stock = l.currentStock?.attributes?.quantity;
    const count = sellerCart[l.id.uuid]?.count || 0;
    // Require enough stock for the quantity in the cart, not merely > 0.
    return typeof stock === 'number' && stock >= count;
  });

/**
 * The listings in this seller's cart that no longer exist (closed or deleted
 * since they were added), out of those the last cart fetch found missing.
 *
 * @param {Object} sellerCart
 * @param {Array<string>} unavailableListingIds - CartPage state
 * @returns {Array<string>}
 */
export const getUnavailableListingIds = (sellerCart, unavailableListingIds = []) =>
  getSellerCartListingIds(sellerCart || {}).filter(id => unavailableListingIds.includes(id));

/**
 * Whether the delivery method chosen for this seller's cart can still be used:
 * the seller offers it and it can be priced for the books now in the basket.
 * A choice can go stale, e.g. "Seller arranges postage" when a book without a
 * seller price is added; the cart then asks the buyer to choose again.
 *
 * @param {Object} sellerCart
 * @param {Array} sellerListings - with author included
 * @returns {boolean}
 */
export const isSellerCartMethodAvailable = (sellerCart, sellerListings) => {
  const method = sellerCart?.deliveryMethod;
  if (!method || sellerListings.length === 0) {
    return false;
  }
  const sellerShippingPreferences =
    sellerListings[0]?.author?.attributes?.profile?.publicData?.shippingPreferences || [];
  const items = sellerListings.map(l => {
    const { parcelSize, book_type: bookFormat, selfShippingPrice } = l.attributes.publicData || {};
    return {
      bookSize: parcelSize,
      bookFormat,
      count: sellerCart[l.id.uuid]?.count || 0,
      selfShippingPrice,
    };
  });
  return getCartDeliveryOptions({ sellerShippingPreferences, items }).some(o => o.key === method);
};

/**
 * Whether a seller's cart can go straight to checkout: a delivery method is
 * chosen and still available, every book still exists and every book is in stock. Checkout cannot
 * price a basket without a method or with a closed listing in it, so sending one
 * there only produces an error page. Shared by the cart page and the topbar
 * drawer so both apply the same rule.
 *
 * @param {Object} sellerCart
 * @param {Array} sellerListings
 * @param {Array<string>} unavailableListingIds - CartPage state
 * @returns {boolean}
 */
export const canCheckoutSellerCart = (sellerCart, sellerListings, unavailableListingIds = []) =>
  isSellerCartMethodAvailable(sellerCart, sellerListings) &&
  getCartItemCount(sellerCart) > 0 &&
  getUnavailableListingIds(sellerCart, unavailableListingIds).length === 0 &&
  hasStockForSellerCart(sellerCart, sellerListings);

/**
 * Send the buyer to CheckoutPage for one seller's cart.
 *
 * Cart checkout is always single-seller: a Sharetribe transaction is bound to
 * one listing and Stripe pays one provider, so the first listing acts as the
 * "main" listing and the rest travel in orderData.cart.
 *
 * Shared by CartPage and the topbar cart drawer so both build identical
 * checkout state.
 *
 * @param {Object} params
 * @param {string} params.authorId - Seller whose cart is being checked out
 * @param {Object} params.sellerCart - That seller's cart entry
 * @param {Array} params.listings - Listing entities to resolve cart ids against
 * @param {Object} params.routeConfiguration
 * @param {Object} params.history
 * @param {Function} params.callSetInitialValues - Dispatching wrapper
 * @param {Function} params.onInitializeCardPaymentData
 * @param {string?} params.voucherCode - Applied voucher, re-validated server-side
 * @returns {boolean} true if navigation happened
 */
export const goToCartCheckout = params => {
  const {
    authorId,
    sellerCart,
    listings,
    routeConfiguration,
    history,
    callSetInitialValues,
    onInitializeCardPaymentData,
    voucherCode = null,
  } = params;

  const listingIds = getSellerCartListingIds(sellerCart);
  const primaryListing = listings.find(l => l.id.uuid === listingIds[0]);
  if (!primaryListing) {
    return false;
  }

  const cartData = {};
  listingIds.forEach(id => {
    cartData[id] = sellerCart[id];
  });
  cartData.deliveryMethod = sellerCart.deliveryMethod;

  const initialValues = {
    listing: primaryListing,
    orderData: {
      quantity: getCartItemCount(sellerCart),
      deliveryMethod: sellerCart.deliveryMethod,
      cart: cartData,
      ...(voucherCode ? { voucherCode } : {}),
      // The seller whose cart this is; used to clear it after a successful
      // checkout.
      cartSellerId: authorId,
      // Display only: lets checkout list every book, not just the primary
      // listing. The cart checkout sends only `cart` and `voucherCode` on.
      cartListings: listingIds.map(id => listings.find(l => l.id.uuid === id)).filter(Boolean),
    },
    confirmPaymentError: null,
  };

  const { setInitialValues } = findRouteByRouteName('CheckoutPage', routeConfiguration);
  callSetInitialValues(setInitialValues, initialValues);
  onInitializeCardPaymentData();

  history.push(
    createResourceLocatorString(
      'CheckoutPage',
      routeConfiguration,
      { id: primaryListing.id.uuid, slug: createSlug(primaryListing.attributes.title) },
      {}
    )
  );
  return true;
};
